/** @jsx jsx */
/* eslint-disable filenames/match-exported */
import { HTMLContent, Rating } from '@datacamp/le-shared-components';
import { Button } from '@datacamp/waffles/button';
import { Heading } from '@datacamp/waffles/heading';
import { hexToRgba } from '@datacamp/waffles/helpers';
import { Checkmark, Cross } from '@datacamp/waffles/icon';
import { Text } from '@datacamp/waffles/text';
import { tokens } from '@datacamp/waffles/tokens';
import { css, jsx } from '@emotion/react';
import startCase from 'lodash/startCase';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { withRouter } from 'react-router';

import config from '../../../config';
import Shortcuts from '../../../containers/AssignmentShortcuts';
import { I18N_PLACEHOLDER } from '../../../i18n';
import { useSelector } from '../../../interfaces/State';
import * as selectors from '../../../redux/selectors';

import ExerciseCompletedXp from './ExerciseCompletedXp';

type RateContentProps = {
  canRateChapter: boolean;
  showChapterRating: (...args: any[]) => any;
  submitRating: (...args: any[]) => any;
};

const RateContent: React.FC<RateContentProps> = ({
  canRateChapter,
  showChapterRating,
  submitRating,
}) => {
  const { t } = useTranslation();
  const onRate = (ratingValue: any) => {
    submitRating(ratingValue);
    if (canRateChapter) {
      showChapterRating();
    }
  };

  return (
    <div
      data-cy="exercise-rating"
      css={{ marginBottom: tokens.spacing.large, color: tokens.colors.white }}
    >
      <div>
        <Text
          as="p"
          css={{
            marginBottom: tokens.spacing.small,
            color: tokens.colors.white,
          }}
        >
          {t('Assignment.ExerciseCompleted.RateContent.text')}
        </Text>
      </div>
      <Rating onRate={onRate} />
    </div>
  );
};

const getProgress = ({
  canRateChapter,
  nextExPath,
  shouldSeeExerciseRating,
}: any) => ({
  canRateChapter,
  shouldShowExerciseRating: shouldSeeExerciseRating,
  pathToNext: canRateChapter ? null : nextExPath,
});

type ExerciseCompletedProps = {
  canRateChapter: boolean;
  courseId?: number;
  id: number;
  isNavigatingToNextExercise: boolean;
  message: string;
  nextExPath: string;
  onClose: (...args: any[]) => any;
  onFirstTimeCompletedChapters: (...args: any[]) => any;
  onNext: (...args: any[]) => any;
  shouldSeeContinueButton: boolean;
  shouldSeeExerciseRating: boolean;
  showChapterRating: (...args: any[]) => any;
  submitRating: (...args: any[]) => any;
  userId?: number;
  userProgress: {
    chapter: unknown;
    course: unknown;
    exercise: unknown;
  };
  window?: unknown;
  xpWon: number;
};

const ExerciseCompleted: React.FC<ExerciseCompletedProps> = ({
  canRateChapter,
  id,
  isNavigatingToNextExercise,
  message,
  nextExPath,
  onClose,
  onFirstTimeCompletedChapters,
  onNext,
  shouldSeeContinueButton,
  shouldSeeExerciseRating,
  showChapterRating,
  submitRating,
  userProgress,
  window: exerciseWindow,
  xpWon,
}) => {
  const { t } = useTranslation(['translation', 'common']);
  const handleClickContinue = useCallback(
    (props: any) =>
      props.progress.canRateChapter ? props.showChapterRating : props.onNext,
    [],
  );

  const continueButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const continueButton = continueButtonRef.current;
    if (continueButton != null) {
      // Make sure the focus after other Components got the focus
      setImmediate(() => continueButton.focus());
    }
  }, []);

  const onExit = useCallback(
    (e: any) => {
      e.preventDefault();
      onClose({ id });
    },
    [onClose, id],
  );

  useEffect(() => {
    return () => {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'completed' does not exist on type '{}'.
      if (userProgress.chapter.completed || userProgress.course.completed) {
        onFirstTimeCompletedChapters();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = getProgress({
    nextExPath,
    canRateChapter,
    shouldSeeExerciseRating,
  });

  const [
    aiIncorrectSubmissionsRated,
    setAiIncorrectSubmissionsRated,
  ] = useState(false);
  const userUsedAiIncorrectSubmissions = useSelector(
    selectors.selectUserUsedAiIncorrectSubmissions,
  );
  const userUsedAiErrorExplanation = useSelector(
    selectors.selectUserUsedAiErrorExplanation,
  );
  const userUsedAiIncorrectSubmissionsButNotErrorExplanation =
    userUsedAiIncorrectSubmissions && !userUsedAiErrorExplanation;
  const showAiIncorrectSubmissionsRating =
    userUsedAiIncorrectSubmissionsButNotErrorExplanation &&
    !progress.shouldShowExerciseRating;
  const userId = useSelector(selectors.selectUserSettings).get('id');
  const courseId = useSelector(selectors.selectCourse).get('id');

  useEffect(() => {
    if (window.dataLayer && showAiIncorrectSubmissionsRating) {
      window.dataLayer.push({
        event: 'heap_event',
        heap_event_name:
          'Learn - Campus - AI Incorrect Submissions Rating Exercise Completed Shown',
        heap_event_properties: {
          app_id: config.appName,
          event_category: 'learning',
          identity: userId,
          course_id: courseId,
        },
      });
    }
  }, [courseId, showAiIncorrectSubmissionsRating, userId]);

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 1000,
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        overflowY: 'auto',
        background: hexToRgba(tokens.colors.navy, 0.95),
        borderRadius: tokens.borderRadius.medium,
      }}
      data-cy="exercise-completed-pane"
    >
      <Button
        variant="plain"
        icon={<Cross />}
        aria-label="Close"
        onClick={onExit}
        inverted
        css={{
          color: tokens.colors.white,
          position: 'absolute',
          top: tokens.spacing.small,
          right: tokens.spacing.small,
        }}
      />
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          flexGrow: 1,
          maxWidth: 500,
        }}
      >
        <ExerciseCompletedXp xpWon={xpWon} />
        <Text
          as="div"
          css={{
            marginBottom: tokens.spacing.medium,
            paddingLeft: tokens.spacing.medium,
            paddingRight: tokens.spacing.medium,
            color: tokens.colors.white,
            lineHeight: tokens.lineHeights.relaxed,
            textAlign: 'center',
            'p > code': {
              mixBlendMode: 'normal',
            },
          }}
        >
          <HTMLContent mathJaxEnabled customTag="p" html={message} />
        </Text>
        {progress.shouldShowExerciseRating && (
          <RateContent
            {...{
              submitRating: submitRating(progress.pathToNext),
              canRateChapter: progress.canRateChapter,
              showChapterRating,
            }}
          />
        )}
        {shouldSeeContinueButton && (
          <div css={{ display: 'flex', flexDirection: 'column' }}>
            <Trans
              i18nKey="Assignment.ExerciseCompleted.continueButton"
              components={{
                text: (
                  <Text
                    css={{
                      color: tokens.colors.white,
                      fontSize: tokens.fontSizes.large,
                      fontWeight: tokens.fontWeights.bold,
                      letterSpacing: tokens.letterSpacing.relaxed,
                      textTransform: 'uppercase',
                      marginBottom: tokens.spacing.small,
                    }}
                  >
                    {I18N_PLACEHOLDER}
                  </Text>
                ),
                btn: (
                  <Button
                    ref={continueButtonRef}
                    data-cy="next-exercise-button"
                    variant="primary"
                    onClick={handleClickContinue({
                      progress,
                      showChapterRating,
                      onNext,
                    })}
                    isLoading={isNavigatingToNextExercise}
                    disabled={
                      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
                      exerciseWindow.type === 'fetching' || exerciseWindow.show
                    }
                    inverted
                  >
                    {I18N_PLACEHOLDER}
                  </Button>
                ),
              }}
            />
          </div>
        )}
        {showAiIncorrectSubmissionsRating && (
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: tokens.spacing.large,
              alignItems: 'center',
            }}
          >
            {aiIncorrectSubmissionsRated ? (
              <Heading
                css={{
                  paddingLeft: tokens.spacing.medium,
                  paddingRight: tokens.spacing.medium,
                  color: tokens.colors.white,
                  lineHeight: tokens.lineHeights.relaxed,
                }}
              >
                {t(
                  'Assignment.ExerciseCompleted.AiIncorrectSubmissionRating.rated',
                )}
              </Heading>
            ) : (
              <>
                <Heading
                  css={{
                    paddingLeft: tokens.spacing.medium,
                    paddingRight: tokens.spacing.medium,
                    color: tokens.colors.white,
                    lineHeight: tokens.lineHeights.relaxed,
                  }}
                >
                  {t(
                    'Assignment.ExerciseCompleted.AiIncorrectSubmissionRating.question',
                  )}
                </Heading>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <Button
                    css={css`
                      color: ${tokens.colors.white};
                    `}
                    data-cy="ai-incorrect-submissions-exercise-completed-yes-button"
                    aria-label={startCase(t('yes', { ns: 'common' }))}
                    variant="plain"
                    onClick={() => setAiIncorrectSubmissionsRated(true)}
                    iconLeft={<Checkmark color={tokens.colors.white} />}
                  >
                    {startCase(t('yes', { ns: 'common' }))}
                  </Button>
                  <Button
                    css={css`
                      color: ${tokens.colors.white};
                    `}
                    data-cy="ai-incorrect-submissions-exercise-completed-no-button"
                    aria-label={startCase(t('no', { ns: 'common' }))}
                    variant="plain"
                    onClick={() => setAiIncorrectSubmissionsRated(true)}
                    iconLeft={<Cross color={tokens.colors.red} />}
                  >
                    {startCase(t('no', { ns: 'common' }))}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {progress.shouldShowExerciseRating && <Shortcuts />}
    </div>
  );
};

// @ts-expect-error fix later, leftover from when codebase was in js
export default withRouter(ExerciseCompleted);
